/**
 * OTA (Over-The-Air) Update Types
 *
 * Type definitions for the OTA update system including API contracts,
 * service interfaces, and internal state management.
 */

// =============================================================================
// API TYPES - Server Contract
// =============================================================================

/**
 * Response from GET /api/ota/check endpoint
 */
export interface OtaCheckResponse {
  /** Whether an update is available for this device */
  update_available: boolean;
  /** Release information (present only if update_available is true) */
  release?: OtaReleaseDto;
  /** Current version the device is running */
  current_version: string;
  /** Server timestamp */
  timestamp: string;
}

/**
 * Release DTO from server
 */
export interface OtaReleaseDto {
  /** Unique release ID */
  id: string;
  /** Version string (e.g., "1.2.0") */
  version: string;
  /** Build number */
  build_number: number;
  /** Target platform */
  // 'windows'/'linux' = wrapper desktop (Tauri). O mapeamento pro wire do
  // servidor ('desktop-windows'/'desktop-linux', plano §3.6) é feito na Fase 4.
  platform:
    | 'tizen'
    | 'webos'
    | 'android'
    | 'ios'
    | 'browser'
    | 'embedded'
    | 'windows'
    | 'linux';
  /** Download URL for the bundle */
  bundle_url: string;
  /** SHA-256 checksum for validation (prefixed with "sha256:") */
  bundle_checksum: string;
  /** Bundle file size in bytes */
  bundle_size_bytes: number;
  /** Whether this update is mandatory */
  is_mandatory: boolean;
  /** Minimum version required before this update can be applied */
  min_supported_version: string | null;
  /** Release notes (markdown) */
  release_notes: string | null;
  /** Release date (ISO 8601) */
  released_at: string;
}

/**
 * Request body for POST /api/ota/status endpoint
 */
export interface OtaStatusRequest {
  /** Device unique identifier */
  device_id: string;
  /** Release ID being reported on */
  release_id: string;
  /** Current status of the update */
  status: OtaUpdateStatus;
  /** Progress percentage (0-100), used during download */
  progress_percent?: number;
  /** Error message if status is FAILED */
  error_message?: string;
  /** Timestamp of the status update */
  timestamp: string;
}

/**
 * Response from POST /api/ota/status endpoint
 */
export interface OtaStatusResponse {
  /** Server acknowledged the status */
  success: boolean;
  /** Whether the device should retry the update */
  should_retry?: boolean;
  /** Seconds to wait before retrying (exponential backoff) */
  retry_after_seconds?: number;
  /** Server message (for logging) */
  message?: string;
}

// =============================================================================
// CLIENT TYPES
// =============================================================================

/**
 * Client-side release model (transformed from DTO)
 */
export interface OtaRelease {
  id: string;
  version: string;
  buildNumber: number;
  // 'windows'/'linux' = wrapper desktop (Tauri). O mapeamento pro wire do
  // servidor ('desktop-windows'/'desktop-linux', plano §3.6) é feito na Fase 4.
  platform:
    | 'tizen'
    | 'webos'
    | 'android'
    | 'ios'
    | 'browser'
    | 'embedded'
    | 'windows'
    | 'linux';
  bundleUrl: string;
  bundleChecksum: string;
  bundleSizeBytes: number;
  isMandatory: boolean;
  minSupportedVersion: string | null;
  releaseNotes: string | null;
  releasedAt: string;
}

/**
 * Update status lifecycle
 */
export enum OtaUpdateStatus {
  /** Update detected, awaiting download */
  PENDING = 'pending',
  /** Download in progress */
  DOWNLOADING = 'downloading',
  /** Download complete, checksum verified */
  DOWNLOADED = 'downloaded',
  /** Installing the bundle via platform service */
  INSTALLING = 'installing',
  /** Update successfully installed and app restarted */
  SUCCESS = 'success',
  /** Update failed at any stage */
  FAILED = 'failed',
  /** Update was rolled back */
  ROLLED_BACK = 'rolled_back',
}

/**
 * Error codes for OTA failures
 */
export enum OtaErrorCode {
  /** Network error during download */
  NETWORK_ERROR = 'NETWORK_ERROR',
  /** Checksum mismatch after download */
  CHECKSUM_MISMATCH = 'CHECKSUM_MISMATCH',
  /** Not enough disk space */
  INSUFFICIENT_STORAGE = 'INSUFFICIENT_STORAGE',
  /** Platform installation failed */
  INSTALLATION_FAILED = 'INSTALLATION_FAILED',
  /** Update incompatible with current version */
  VERSION_INCOMPATIBLE = 'VERSION_INCOMPATIBLE',
  /** Unknown error */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * OTA Service Configuration
 */
export interface OtaServiceConfig {
  /** Base URL for OTA API (e.g., "http://localhost:8082") */
  apiUrl: string;
  /** Unique device identifier */
  deviceId: string;
  /** Current app version */
  currentVersion: string;
  /** Target platform */
  // 'windows'/'linux' = wrapper desktop (Tauri). O mapeamento pro wire do
  // servidor ('desktop-windows'/'desktop-linux', plano §3.6) é feito na Fase 4.
  platform:
    | 'tizen'
    | 'webos'
    | 'android'
    | 'ios'
    | 'browser'
    | 'embedded'
    | 'windows'
    | 'linux';
  /** Check interval in milliseconds (default: 30 minutes) */
  checkIntervalMs?: number;
  /** Maximum retry attempts for download (default: 3) */
  maxRetryAttempts?: number;
  /** Initial retry delay in milliseconds (default: 5000) */
  initialRetryDelayMs?: number;
}

// =============================================================================
// CALLBACK TYPES
// =============================================================================

/**
 * Callback when an update is available
 */
export type UpdateAvailableCallback = (release: OtaRelease) => void;

/**
 * Callback for download progress updates
 */
export type DownloadProgressCallback = (
  progress: number,
  bytesDownloaded: number,
  totalBytes: number,
) => void;

/**
 * Callback when update completes successfully
 */
export type UpdateCompleteCallback = () => void;

/**
 * Callback when update fails
 */
export type UpdateFailedCallback = (
  error: Error,
  errorCode: OtaErrorCode,
) => void;

/**
 * Callback when update status changes
 */
export type UpdateStatusChangeCallback = (status: OtaUpdateStatus) => void;

// =============================================================================
// SERVICE INTERFACE
// =============================================================================

/**
 * OTA Service Interface
 *
 * Manages the complete lifecycle of OTA updates:
 * - Periodic checking for updates
 * - Background downloading with progress tracking
 * - Checksum validation
 * - Status reporting to server
 * - Platform-specific installation
 */
export interface IOtaService {
  // Lifecycle Methods
  /**
   * Initialize the OTA service.
   * Sets up polling and event listeners.
   */
  init(): void;

  /**
   * Start checking for updates periodically.
   */
  startChecking(): void;

  /**
   * Stop checking for updates.
   */
  stopChecking(): void;

  /**
   * Clean up resources and stop all operations.
   */
  destroy(): void;

  // Update Operations
  /**
   * Manually check for available updates.
   * @returns Promise resolving to check response
   */
  checkForUpdate(): Promise<OtaCheckResponse>;

  /**
   * Download an update release.
   * Validates checksum after download.
   * @param release - The release to download
   * @returns Promise resolving when download complete
   */
  downloadUpdate(release: OtaRelease): Promise<void>;

  /**
   * Install a downloaded update.
   * Delegates to platform service for installation.
   * @param bundlePath - Local path to the downloaded bundle
   * @returns Promise resolving when installation complete
   */
  installUpdate(bundlePath: string): Promise<void>;

  /**
   * Pause an ongoing download.
   */
  pauseDownload(): void;

  /**
   * Resume a paused download.
   */
  resumeDownload(): void;

  // Status Reporting
  /**
   * Report status to server.
   * @param status - Current update status
   * @param progress - Progress percentage (0-100)
   * @param error - Error message if status is FAILED
   * @returns Promise resolving to server response
   */
  reportStatus(
    status: OtaUpdateStatus,
    progress?: number,
    error?: string,
  ): Promise<OtaStatusResponse>;

  // Event Listeners
  /**
   * Register callback for when update is available.
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  onUpdateAvailable(callback: UpdateAvailableCallback): () => void;

  /**
   * Register callback for download progress.
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  onDownloadProgress(callback: DownloadProgressCallback): () => void;

  /**
   * Register callback for update completion.
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  onUpdateComplete(callback: UpdateCompleteCallback): () => void;

  /**
   * Register callback for update failure.
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  onUpdateFailed(callback: UpdateFailedCallback): () => void;

  /**
   * Register callback for status changes.
   * @param callback - Callback function
   * @returns Unsubscribe function
   */
  onStatusChange(callback: UpdateStatusChangeCallback): () => void;

  // State Getters
  /**
   * Get current update status.
   */
  getCurrentStatus(): OtaUpdateStatus | null;

  /**
   * Get current release being processed.
   */
  getCurrentRelease(): OtaRelease | null;

  /**
   * Get current download progress (0-100).
   */
  getDownloadProgress(): number;

  /**
   * Check if an update is currently in progress.
   */
  isUpdateInProgress(): boolean;
}
